import { useCurrencyConversionRate } from '@apps/card/hooks/useCurrencyConversionRate';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useStationeryCurrency } from '@shared/utils/currency/useStationeryCurrency';
import { useOrderPriceConversion } from '@apps/card/components/Price/OrderPriceConversion';
import { addError } from '@shared/utils/logger';
import { applyConversionRate } from '@apps/card/utils/orderCurrencyConversion';
import { useCallback } from 'react';

export type CurrencyConverterOptions = Readonly<{
  precision?: number;
  currency?: CurrencyCodeType;
}>;

/**
 * Converts a USD minor-units amount into the currency that applies here.
 *
 * This hook owns the question of *which rate* applies; the arithmetic itself is
 * `applyConversionRate` either way. There are two rate sources, in priority order:
 *
 * 1. The rate an order was billed at, when an `OrderPriceConversionProvider` is above us.
 * 2. The live geo-based rate for the currency picker's selection.
 */
export const useCurrencyConverter = () => {
  const orderConversion = useOrderPriceConversion();
  const currency = useStationeryCurrency();
  const { data: currencyRateData, loading: isCurrencyConversionRateLoading } = useCurrencyConversionRate(currency);

  return useCallback(
    (amount: number, { currency: currencyFromArg, precision = 1000 }: CurrencyConverterOptions = {}) => {
      // if free, no conversion needed, return 0
      if (amount === 0) return 0;

      // A placed order carries the rate it was billed at, so that rate wins outright — including
      // over the feature flag, which governs whether we convert live prices for a shopper, not
      // whether we report a completed transaction honestly.
      if (orderConversion) return applyConversionRate(amount, orderConversion.rate, precision);

      const targetCurrency = currencyFromArg ?? currency;
      const toData = currencyRateData;

      if (!toData) {
        // We only want to throw an error if the target currency is not USD
        // Maybe we should throw an error as well once all conversions are ready?
        if (targetCurrency !== DEFAULT_CURRENCY && !isCurrencyConversionRateLoading) {
          addError(`Currency rate data not found for: ${targetCurrency}`);
        }
        // If currency not found, return the amount (no conversion)
        return amount;
      }

      return applyConversionRate(amount, toData, precision);
    },
    [currency, currencyRateData, isCurrencyConversionRateLoading, orderConversion],
  );
};
